import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { confirmDialog } from "../../../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { getPrivacyPolicy, savePrivacyPolicy } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminCMSPrivacy = () => {
  const [sections, setSections] = useState([]);
  const [originalSections, setOriginalSections] = useState([]);
  const [iconOptions, setIconOptions] = useState([]);
  const [originalIconOptions, setOriginalIconOptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Validation & Change tracking
  const isFormInvalid = useMemo(() => {
    const hasInvalidSection = sections.some(
      (s) => !s.title?.trim() || !s.content?.trim(),
    );
    const hasInvalidIcon = iconOptions.some((icon) => !icon.trim());
    return hasInvalidSection || hasInvalidIcon;
  }, [sections, iconOptions]);

  const isChanged = useMemo(() => {
    // Check icon options changes
    if (iconOptions.length !== originalIconOptions.length) return true;
    if (iconOptions.some((icon, i) => icon !== originalIconOptions[i]))
      return true;

    // Check sections changes
    if (sections.length !== originalSections.length) return true;
    return sections.some((s, i) => {
      const orig = originalSections[i];
      if (
        s.title !== orig.title ||
        s.content !== orig.content ||
        s.iconName !== orig.iconName
      )
        return true;

      const currList = s.listItems || [];
      const origList = orig.listItems || [];
      if (currList.length !== origList.length) return true;
      return currList.some((item, j) => item !== origList[j]);
    });
  }, [sections, originalSections, iconOptions, originalIconOptions]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { sections: list, iconOptions: opts } = await getPrivacyPolicy();
        if (!mounted) return;
        const mappedSections = (Array.isArray(list) ? list : []).map((s) => ({
          ...s,
          listItems: s.listItems ? [...s.listItems] : [],
          titleTouched: true,
          contentTouched: true,
        }));
        const mappedIcons = Array.isArray(opts) ? [...opts] : [];

        setSections(JSON.parse(JSON.stringify(mappedSections)));
        setOriginalSections(JSON.parse(JSON.stringify(mappedSections)));
        setIconOptions(JSON.parse(JSON.stringify(mappedIcons)));
        setOriginalIconOptions(JSON.parse(JSON.stringify(mappedIcons)));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load Privacy Policy",
        );
        setSections([]);
        setOriginalSections([]);
        setIconOptions([]);
        setOriginalIconOptions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const addSection = () => {
    const id = `section-${Date.now()}`;
    const defaultIcon = iconOptions[0] || "Shield";
    setSections([
      ...sections,
      {
        id,
        title: "",
        iconName: defaultIcon,
        content: "",
        listItems: [],
        titleTouched: false,
        contentTouched: false,
      },
    ]);
  };

  const updateSection = (index, field, value) => {
    const next = [...sections];
    next[index] = {
      ...next[index],
      [field]: value,
      [`${field}Touched`]: true,
    };
    setSections(next);
  };

  const updateListItem = (sectionIndex, itemIndex, value) => {
    const next = [...sections];
    const list = [...(next[sectionIndex].listItems || [])];
    list[itemIndex] = value;
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const addListItem = (sectionIndex) => {
    const next = [...sections];
    const list = [...(next[sectionIndex].listItems || [])];
    list.push("");
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const removeListItem = (sectionIndex, itemIndex) => {
    const next = [...sections];
    const list = (next[sectionIndex].listItems || []).filter(
      (_, i) => i !== itemIndex,
    );
    next[sectionIndex] = { ...next[sectionIndex], listItems: list };
    setSections(next);
  };

  const removeSection = async (index) => {
    const confirmed = await confirmDialog({
      title: "Remove section?",
      text: "This section will be removed from Privacy Policy.",
    });
    if (!confirmed) return;
    setSections(sections.filter((_, i) => i !== index));
    Swal.fire({
      icon: "success",
      title: "Removed",
      text: "This Section removed successfully.",
      confirmButtonColor: "#38915c",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const addIconOption = () => {
    setIconOptions([...iconOptions, ""]);
  };

  const updateIconOption = (index, value) => {
    const next = [...iconOptions];
    next[index] = value;
    setIconOptions(next);
  };

  const removeIconOption = (index) => {
    setIconOptions(iconOptions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Sections",
        text: "Please fill in all titles, content, and icons before saving.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    const valid = sections.filter((s) => (s.title || "").trim());
    const trimmedIcons = iconOptions
      .map((x) => (x || "").trim())
      .filter(Boolean);
    try {
      const { sections: savedSections, iconOptions: savedIcons } =
        await savePrivacyPolicy({
          sections: valid,
          iconOptions: trimmedIcons,
        });

      const updatedSections = (
        Array.isArray(savedSections) ? savedSections : []
      ).map((s) => ({
        ...s,
        listItems: s.listItems ? [...s.listItems] : [],
      }));
      const updatedIcons = Array.isArray(savedIcons) ? [...savedIcons] : [];

      setSections(JSON.parse(JSON.stringify(updatedSections)));
      setOriginalSections(JSON.parse(JSON.stringify(updatedSections)));
      setIconOptions(JSON.parse(JSON.stringify(updatedIcons)));
      setOriginalIconOptions(JSON.parse(JSON.stringify(updatedIcons)));

      setError("");
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Privacy Policy updated successfully",
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to save Privacy Policy",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Privacy Policy</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage platform privacy details. All fields are required.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Available icons (from backend)
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Lucide React icon export names (e.g. Shield, Mail). Used in the
            section icon dropdown.
          </p>
          {iconOptions.length === 0 && (
            <p className="text-sm text-gray-500 mb-3">
              No icons configured — add names below, then Save.
            </p>
          )}
          <div className="space-y-2">
            {iconOptions.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => updateIconOption(i, e.target.value)}
                  className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g. Shield"
                />
                <button
                  type="button"
                  onClick={() => removeIconOption(i)}
                  className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                  title="Remove"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addIconOption}
            className="mt-3 text-sm text-primary-600 hover:underline"
          >
            + Add icon name
          </button>
        </div>

        {sections.length === 0 && (
          <div className="p-6 rounded-xl border-2 border-dashed border-gray-200 bg-white text-gray-600 text-center">
            Data is not available
          </div>
        )}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="p-6 rounded-xl border-2 border-gray-200 bg-white"
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <span className="text-sm font-medium text-gray-500">
                Section {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                title="Remove section"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <div className="grid gap-4">
              <TextInput
                label="Title"
                value={section.title || ""}
                onChange={(e) => updateSection(index, "title", e.target.value)}
                placeholder="Section title"
                error={
                  section.titleTouched &&
                  !section.title?.trim() &&
                  "Title is required"
                }
                required
              />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Icon (for display)
                </label>
                {iconOptions.length > 0 ? (
                  <select
                    value={
                      section.iconName &&
                      [...new Set([...iconOptions, section.iconName])].includes(
                        section.iconName,
                      )
                        ? section.iconName
                        : iconOptions[0]
                    }
                    onChange={(e) =>
                      updateSection(index, "iconName", e.target.value)
                    }
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    {[
                      ...new Set(
                        [...iconOptions, section.iconName].filter(Boolean),
                      ),
                    ].map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <TextInput
                    label=""
                    value={section.iconName || ""}
                    onChange={(e) =>
                      updateSection(index, "iconName", e.target.value)
                    }
                    placeholder="Lucide icon name (e.g. Shield)"
                  />
                )}
              </div>
              <TextInput
                label="Content"
                value={section.content || ""}
                onChange={(e) =>
                  updateSection(index, "content", e.target.value)
                }
                placeholder="Section content"
                error={
                  section.contentTouched &&
                  !section.content?.trim() &&
                  "Content is required"
                }
                multiline
                rows={3}
                required
              />
              {(section.listItems || []).length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    List items
                  </label>
                  <div className="space-y-2">
                    {(section.listItems || []).map((item, i) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) =>
                            updateListItem(index, i, e.target.value)
                          }
                          className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                          placeholder="List item"
                        />
                        <button
                          type="button"
                          onClick={() => removeListItem(index, i)}
                          className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addListItem(index)}
                    className="mt-2 text-sm text-primary-600 hover:underline"
                  >
                    + Add list item
                  </button>
                </div>
              )}
              {(!section.listItems || section.listItems.length === 0) && (
                <button
                  type="button"
                  onClick={() => addListItem(index)}
                  className="text-sm text-primary-600 hover:underline"
                >
                  + Add bullet list (optional)
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="flex gap-4 mt-8 items-stretch">
          <button
            type="button"
            onClick={addSection}
            className="w-[70%] flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
          >
            <Plus size={20} />
            Add section
          </button>
          <div className="w-[30%]">
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              className="w-full !h-full shadow-md"
              disabled={saving || !isChanged || isFormInvalid}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCMSPrivacy;
